import { useState, useEffect, useCallback, useRef } from 'react';
import { SESSION_KEYS } from '@/config/constants';
import { debug } from '@/utils/debug';

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds (default: 5 minutes)
  key?: string; // Custom cache key
  invalidateOn?: string[]; // List of events that should invalidate cache
  persistToStorage?: boolean; // Whether to persist to sessionStorage
}

interface CacheState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  cacheAge: number; // Age in seconds
  isCached: boolean;
}

interface UseCacheReturn<T> extends CacheState<T> {
  refresh: () => Promise<void>;
  invalidate: () => void;
  updateCache: (data: T) => void;
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_CHECK_INTERVAL = 1000; // Check cache age every second

/**
 * Hook for managing cached data with automatic expiration and refresh
 * @param cacheKey - Unique key for the cache entry
 * @param fetcher - Function to fetch fresh data
 * @param options - Cache configuration options
 */
export function useCache<T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
  options: CacheOptions = {}
): UseCacheReturn<T> {
  const {
    ttl = DEFAULT_TTL,
    key = cacheKey,
    invalidateOn = [],
    persistToStorage = true,
  } = options;
  
  const [state, setState] = useState<CacheState<T>>({
    data: null,
    isLoading: false,
    error: null,
    cacheAge: 0,
    isCached: false,
  });
  
  const cacheTimestampRef = useRef<number>(0);
  const ageIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const memberUuid = sessionStorage.getItem(SESSION_KEYS.MEMBER_UUID) || '';

  // Generate full cache key with member UUID
  const fullCacheKey = `${SESSION_KEYS.CACHE_PREFIX}_${memberUuid}_${key}`;
  const timestampKey = `${fullCacheKey}_ts`;
  
  // Load data from cache
  const loadFromCache = useCallback(() => {
    if (!persistToStorage) return null;
    
    try {
      const cachedData = sessionStorage.getItem(fullCacheKey);
      const timestamp = sessionStorage.getItem(timestampKey);
      
      if (cachedData && timestamp) {
        const parsedTimestamp = parseInt(timestamp, 10);
        const age = Date.now() - parsedTimestamp;
        
        if (age < ttl) {
          debug.log(`useCache: Loaded from cache [${key}]`, { age: age / 1000 });
          cacheTimestampRef.current = parsedTimestamp;
          return {
            data: JSON.parse(cachedData),
            timestamp: parsedTimestamp,
            age: Math.floor(age / 1000),
          };
        } else {
          debug.log(`useCache: Cache expired [${key}]`, { age: age / 1000 });
          sessionStorage.removeItem(fullCacheKey);
          sessionStorage.removeItem(timestampKey);
        }
      }
    } catch (error) {
      debug.error(`useCache: Error loading from cache [${key}]`, error);
    }
    
    return null;
  }, [fullCacheKey, timestampKey, ttl, key, persistToStorage]);
  
  // Save data to cache
  const saveToCache = useCallback((data: T) => {
    if (!persistToStorage) return;
    
    try {
      const timestamp = Date.now();
      sessionStorage.setItem(fullCacheKey, JSON.stringify(data));
      sessionStorage.setItem(timestampKey, timestamp.toString());
      cacheTimestampRef.current = timestamp;
      debug.log(`useCache: Saved to cache [${key}]`);
    } catch (error) {
      debug.error(`useCache: Error saving to cache [${key}]`, error);
    }
  }, [fullCacheKey, timestampKey, key, persistToStorage]);
  
  // Fetch fresh data
  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      debug.log(`useCache: Fetching fresh data [${key}]`);
      const data = await fetcher();
      
      setState({
        data,
        isLoading: false,
        error: null,
        cacheAge: 0,
        isCached: false,
      });
      
      saveToCache(data);
      
      // Record performance metric
      if (import.meta.env.DEV && window.__DEBUG_STORE__) {
        window.__DEBUG_STORE__.recordPerformance(`cache-fetch-${key}`, Date.now() - cacheTimestampRef.current);
      }
      
      return data;
    } catch (error) {
      debug.error(`useCache: Error fetching data [${key}]`, error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error as Error,
      }));
      throw error;
    }
  }, [fetcher, key, saveToCache]);
  
  // Refresh data (force fetch)
  const refresh = useCallback(async () => {
    debug.log(`useCache: Manual refresh triggered [${key}]`);
    await fetchData();
  }, [fetchData, key]);
  
  // Invalidate cache
  const invalidate = useCallback(() => {
    debug.log(`useCache: Cache invalidated [${key}]`);
    if (persistToStorage) {
      sessionStorage.removeItem(fullCacheKey);
      sessionStorage.removeItem(timestampKey);
    }
    setState(prev => ({ ...prev, data: null, cacheAge: 0, isCached: false }));
    cacheTimestampRef.current = 0;
  }, [fullCacheKey, timestampKey, key, persistToStorage]);
  
  // Update cache with new data
  const updateCache = useCallback((data: T) => {
    debug.log(`useCache: Manual cache update [${key}]`);
    setState({
      data,
      isLoading: false,
      error: null,
      cacheAge: 0,
      isCached: true,
    });
    saveToCache(data);
  }, [key, saveToCache]);
  
  // Initial load effect
  useEffect(() => {
    const cached = loadFromCache();
    
    if (cached) {
      setState({
        data: cached.data,
        isLoading: false,
        error: null,
        cacheAge: cached.age,
        isCached: true,
      });
    } else {
      fetchData();
    }
  }, [loadFromCache, fetchData]);
  
  // Cache age update effect
  useEffect(() => {
    if (state.isCached && cacheTimestampRef.current > 0) {
      ageIntervalRef.current = setInterval(() => {
        const age = Math.floor((Date.now() - cacheTimestampRef.current) / 1000);
        setState(prev => ({ ...prev, cacheAge: age }));

        // Auto-refresh if cache expired
        if (age * 1000 > ttl) {
          debug.log(`useCache: Auto-refreshing expired cache [${key}]`);
          fetchData();
        }
      }, CACHE_CHECK_INTERVAL) as unknown as ReturnType<typeof setInterval>;
      
      return () => {
        if (ageIntervalRef.current) {
          clearInterval(ageIntervalRef.current);
        }
      };
    }
  }, [state.isCached, ttl, fetchData, key]);
  
  // Invalidation events effect
  useEffect(() => {
    if (invalidateOn.length === 0) return;
    
    const handleInvalidation = (event: Event) => {
      if (invalidateOn.includes(event.type)) {
        debug.log(`useCache: Invalidated by event [${key}]`, event.type);
        invalidate();
      }
    };
    
    invalidateOn.forEach(eventType => {
      window.addEventListener(eventType, handleInvalidation);
    });
    
    return () => {
      invalidateOn.forEach(eventType => {
        window.removeEventListener(eventType, handleInvalidation);
      });
    };
  }, [invalidateOn, invalidate, key]);
  
  return {
    ...state,
    refresh,
    invalidate,
    updateCache,
  };
}

// Helper hook for app configuration cache (common use case)
export function useAppConfigCache() {
  const memberUuid = sessionStorage.getItem(SESSION_KEYS.MEMBER_UUID) || '';

  return useCache(
    'app-config',
    async () => {
      const { optimizationApi } = await import('@/services/api/optimization');
      return optimizationApi.loadConfiguration(memberUuid);
    },
    {
      ttl: 5 * 60 * 1000, // 5 minutes
      invalidateOn: ['config-saved'],
    }
  );
}

// Utility to invalidate all caches
export function invalidateAllCaches() {
  const memberUuid = sessionStorage.getItem(SESSION_KEYS.MEMBER_UUID) || '';

  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith(`${SESSION_KEYS.CACHE_PREFIX}_${memberUuid}_`)) {
      sessionStorage.removeItem(key);
    }
  });
  
  debug.log('useCache: All caches invalidated');
  
  // Dispatch event for any listening caches
  window.dispatchEvent(new Event('cache-invalidate-all'));
}